import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteIssueClassification,
  reactivateIssueClassification,
} from "../../services/classificationService";
import useFetchPaginatedIssueClassifications from "../../hooks/useFetchPaginatedClassifications";
import useDebounce from "../../hooks/useDebounce";
import { getFormattedDateTime } from "../../services/utils";
import {
  IconDeleteBlueSvg,
  IconEditBase,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import ButtonComponent from "../../components/Button.Component";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { getConfig } from "../../config/runtimeConfig";

const CLASSIFICATION_FILTER_CONFIG = {
  DEFAULT_STATE: {
    issue_classification_name: "",
    filter_ids: "",
    status_filter: "Active",
  },
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["issue_classification_name", "filter_ids", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const ClassificationList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();

  const canViewClassification = hasPermission("classification", "view");
  const canAddClassification = hasPermission("classification", "add");
  const canEditClassification = hasPermission("classification", "edit");
  const canDeleteClassification = hasPermission("classification", "delete");

  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [classificationToDelete, setClassificationToDelete] =
    useState<any>(null);

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = { ...CLASSIFICATION_FILTER_CONFIG.DEFAULT_STATE };
    CLASSIFICATION_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) initialState[field] = urlValue;
    });
    return initialState;
  });

  const {
    issue_classification_name: effectiveName = "",
    filter_ids: effectiveFilterIds = "",
    status_filter: effectiveStatusFilter = "Active",
  } = searchFilterData || {};
  const debouncedName = useDebounce(effectiveName);
  const effectiveNameSearch =
    debouncedName.trim().length >= 3 ? debouncedName : "";

  // Convert status_filter to isActive boolean
  const getIsActiveValue = (statusFilter: string): boolean | undefined => {
    if (statusFilter === "Active") return true;
    if (statusFilter === "Inactive") return false;
    return undefined; // "All" or empty - no filter
  };

  const [fetchedClassifications, refetchClassifications] =
    useFetchPaginatedIssueClassifications({
      setIsLoading,
      page,
      pSize,
      searchTerm: effectiveNameSearch,
      filterIds: effectiveFilterIds,
      isActive: getIsActiveValue(effectiveStatusFilter),
    });

  useEffect(() => {
    const newParams = new URLSearchParams();
    CLASSIFICATION_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        newParams.set(field, String(value));
      }
    });
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  useEffect(() => {
    const urlFilterState: any = {
      ...CLASSIFICATION_FILTER_CONFIG.DEFAULT_STATE,
    };
    CLASSIFICATION_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) urlFilterState[field] = urlValue;
    });
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const norm = (v: any) =>
        v === null || v === undefined || v === "" ? "" : v;
      return norm(urlFilterState[key]) !== norm(searchFilterData[key]);
    });
    if (hasFilterChanges) setSearchFilterData(urlFilterState);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [effectiveNameSearch, effectiveFilterIds]);

  const handelClickEvent = () => navigate("/classification/add");

  const handleDeleteClassification = (classification: any) => {
    setClassificationToDelete(classification);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!classificationToDelete) return;
    try {
      setIsLoading(true);
      await deleteIssueClassification(classificationToDelete.id);
      showToast(t("classification.toast.deleted"), "success");
      setDeleteConfirmOpen(false);
      setClassificationToDelete(null);
      refetchClassifications();
    } catch (error: any) {
      showToast(
        error?.message || t("classification.toast.failedDelete"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async (classification: any) => {
    try {
      setIsLoading(true);
      await reactivateIssueClassification(classification.id);
      showToast(t("classification.toast.reactivated"), "success");
      refetchClassifications();
    } catch (error: any) {
      console.error("Error reactivating classification:", error);
      showToast(
        error?.response?.data?.detail ||
          t("classification.toast.failedReactivate"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: t("classification.list.columns.id"),
      minWidth: 80,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.id}</span>,
    },
    {
      field: "issue_classification_name",
      headerName: t("classification.list.columns.classificationName"),
      minWidth: 200,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params?.value} />
      ),
    },
    {
      field: "updated_on",
      headerName: t("classification.list.columns.updatedOn"),
      minWidth: 180,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => getFormattedDateTime(params.value),
    },
    {
      field: "is_active",
      headerName: t("classification.list.columns.status"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) =>
        params.value ? (
          <Tooltip
            title={t("classification.list.tooltip.active")}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title={t("classification.list.tooltip.inactive")}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ),
    },
    {
      field: "action",
      headerName: t("classification.list.columns.action"),
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      width: 170,
      sortable: false,
      renderCell: (params: any) => {
        const isActive = params.row.is_active === true;
        return (
          <>
            {isActive && (
              <>
                {canViewClassification && (
                  <Tooltip
                    title={t("classification.list.tooltip.view")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(`/classification/view/${params.row.id}`)
                      }
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditClassification && (
                  <Tooltip
                    title={t("classification.list.tooltip.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(`/classification/edit/${params.row.id}`)
                      }
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canDeleteClassification && (
                  <Tooltip
                    title={t("classification.list.tooltip.delete")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => handleDeleteClassification(params.row)}
                    >
                      <IconDeleteBlueSvg />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {!isActive && canEditClassification && (
              <Tooltip
                title={t("classification.list.tooltip.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(params.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("classification.list.dialog.deleteTitle")}
        dailogDescription={t("classification.list.dialog.deleteDescription", {
          name: classificationToDelete?.issue_classification_name,
        })}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["issue_classification_name", "status_filter"]}
              primaryFields={["issue_classification_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active",
              }}
            />
          </Grid>
          {canAddClassification && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} />
                  {t("classification.list.addButton")}
                </>
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={
            Array.isArray(fetchedClassifications?.items)
              ? fetchedClassifications.items
              : []
          }
          loading={isLoading}
          dataListTitle={t("classification.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fetchedClassifications?.total || 0}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={368}
        />
      </FlexBetween>
    </Box>
  );
};

export default ClassificationList;
